import React from 'react';
import { Field } from '@progress/kendo-react-form';
import { FormDropDownList } from '../Utilities/FormComponents';
import { Loader } from '@progress/kendo-react-indicators';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../messages/en-US';
import AssetTemplateListItemDto from '../../../Models/Assets/AssetTemplateListItemDto';
import AssetImageReadDto from '../../../Models/Assets/AssetImageReadDto';
import { hasGreasePumps } from '../Utilities/AssetDetailsUtils';

interface TemplateSectionProps {
  templates: AssetTemplateListItemDto[];
  assetData: any;
  isEdit: boolean;
  selectedTemplate: AssetTemplateListItemDto | null;
  formState: any;
  assetImageQuery: {
    isLoading: boolean;
    isFetching: boolean;
    data?: AssetImageReadDto;
  };
  onTemplateChange: (event: any) => void;
  onGreasePumpConnectivityChange: (connectivity: 'enabled' | 'disabled', event?: React.MouseEvent) => void;
  templateValidator: (value: any) => string | undefined;
}

const TemplateSection: React.FC<TemplateSectionProps> = ({
  templates,
  assetData,
  isEdit,
  selectedTemplate,
  formState,
  assetImageQuery,
  onTemplateChange,
  onGreasePumpConnectivityChange,
  templateValidator,
}) => {
  const localizationService = useLocalization();

  const templateData = templates.length > 0
    ? [...templates].sort((a, b) => a.name.localeCompare(b.name))
    : assetData.assetTemplate
    ? [assetData.assetTemplate]
    : [];

  const showGreasePump = hasGreasePumps(selectedTemplate, assetData, isEdit);

  return (
    <div className="flex flex-col">
      <div className="flex gap-5">
        <div className="flex-1">
          <Field
            key="template"
            id="template"
            name="template"
            label={localizationService.toLanguageString('custom.template', enMessages.custom.template) + ' *'}
            component={FormDropDownList}
            data={templateData}
            textField="name"
            valueField="id"
            value={isEdit ? assetData.assetTemplate : selectedTemplate}
            defaultValue={isEdit ? assetData.assetTemplate : selectedTemplate}
            onChange={onTemplateChange}
            validator={templateValidator}
            disabled={isEdit}
          />
        </div>
        {showGreasePump && (
          <div className="flex-1">
            <div className="k-form-field">
              <label className="k-label">
                {localizationService.toLanguageString('custom.greasePumpConnectivity', enMessages.custom.greasePumpConnectivity)}
              </label>
              <ButtonGroup>
                <Button
                  togglable={true}
                  selected={formState.greasePumpConnectivity === 'enabled'}
                  onClick={(e) => onGreasePumpConnectivityChange('enabled', e)}
                >
                  {localizationService.toLanguageString('custom.enabled', enMessages.custom.enabled)}
                </Button>
                <Button
                  togglable={true}
                  selected={formState.greasePumpConnectivity === 'disabled'}
                  onClick={(e) => onGreasePumpConnectivityChange('disabled', e)}
                >
                  Disabled
                </Button>
              </ButtonGroup>
            </div>
          </div>
        )}
      </div>
      {assetImageQuery.isLoading || assetImageQuery.isFetching ? (
        <div className="flex justify-center items-center p-4">
          <Loader type="pulsing" />
        </div>
      ) : assetImageQuery.data ? (
        <div className="flex p-4">
          <img
            src={assetImageQuery.data.data}
            alt="Template preview"
            style={{ maxWidth: '150px', objectFit: 'contain' }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default TemplateSection;

